import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import * as authActions from '../state/actions/authentication.actions';

@Component({
  selector: 'app-mfa-verification',
  templateUrl: './mfa-verification.component.html',
  styleUrls: ['./mfa-verification.component.scss']
})
export class MfaVerificationComponent implements OnInit, OnDestroy {
  verificationForm: FormGroup;
  mfaMethod: 'EMAIL' | 'TOTP' | 'BOTH' = 'EMAIL';
  selectedMethod: 'EMAIL' | 'TOTP' | 'BACKUP' = 'EMAIL';
  userEmail: string = '';
  isLoading: boolean = false;
  showBackupCodes: boolean = false;
  resendTimer: number = 0;
  resendInterval: any;
  isCodeSent: boolean = false;

  // Stored login data for completion after MFA verification
  private tempLoginData: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private toastrService: ToastrService,
    public translate: TranslateService,
    private store: Store
  ) {
    this.verificationForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngOnInit(): void {
    // Wait a brief moment for app initialization to complete
    setTimeout(() => {
      this.initializeMfaVerification();
    }, 100);
  }

  private initializeMfaVerification(): void {
    // Get temporary login data from route state or localStorage
    this.tempLoginData = history.state.loginData || JSON.parse(localStorage.getItem('temp_mfa_data') || '{}');

    console.log('MFA Verification - temp data check:', {
      hasState: !!history.state.loginData,
      hasLocalStorage: !!localStorage.getItem('temp_mfa_data'),
      tempData: this.tempLoginData
    });

    if (!this.tempLoginData || !this.tempLoginData.user) {
      // If no temp data, redirect to login
      console.log('No MFA temp data found, redirecting to login');
      this.toastrService.error('Session expired. Please login again.');
      this.router.navigate(['/login']);
      return;
    }

    this.userEmail = this.tempLoginData.user.email;
    this.mfaMethod = this.tempLoginData.user.mfaMethod;

    // Validate required data
    if (!this.userEmail || !this.mfaMethod) {
      console.error('Invalid MFA data:', { email: this.userEmail, method: this.mfaMethod });
      this.toastrService.error('Invalid session data. Please login again.');
      this.router.navigate(['/login']);
      return;
    }

    // Set default method based on available options (but don't auto-send)
    if (this.mfaMethod === 'EMAIL') {
      this.selectedMethod = 'EMAIL';
      // Don't auto-send email code - user must click button
    } else if (this.mfaMethod === 'TOTP') {
      this.selectedMethod = 'TOTP';
    } else if (this.mfaMethod === 'BOTH') {
      this.selectedMethod = 'EMAIL'; // Default to email for BOTH
      // Don't auto-send email code - user must click button
    }

    // Log successful initialization
    console.log('MFA verification initialized successfully', {
      email: this.userEmail,
      method: this.mfaMethod,
      selected: this.selectedMethod
    });
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  // Clear all timers
  clearTimers(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  // Switch between different MFA methods
  switchMethod(method: 'EMAIL' | 'TOTP' | 'BACKUP'): void {
    this.selectedMethod = method;
    this.verificationForm.reset();
    this.isCodeSent = false;

    if (method === 'EMAIL') {
      // Don't auto-send email code - user must click send button
      this.verificationForm.get('code')?.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{6}$/)
      ]);
    } else if (method === 'BACKUP') {
      this.verificationForm.get('code')?.setValidators([
        Validators.required,
        Validators.pattern(/^[A-Z0-9]{5}-[A-Z0-9]{5}$/)
      ]);
    } else {
      this.verificationForm.get('code')?.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{6}$/)
      ]);
    }
    this.verificationForm.get('code')?.updateValueAndValidity();
  }

  // Send email verification code
  sendEmailCode(): void {
    if (this.resendTimer > 0) return;

    this.isLoading = true;

    // Use the correct token property name and send it in Authorization header
    const token = this.tempLoginData.tempToken || this.tempLoginData.mfaToken;

    // Debug logging
    console.log('SendEmailCode Debug:', {
      tempLoginData: this.tempLoginData,
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
      userEmail: this.userEmail
    });

    if (!token) {
      console.error('No token found in tempLoginData');
      this.isLoading = false;
      this.toastrService.error('Authentication token missing. Please login again.');
      this.router.navigate(['/login']);
      return;
    }

    this.http.post(`${environment.apiUrl}/mfa/send-code`, {
      email: this.userEmail
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(
      (response: any) => {
        console.log('Send code success:', response);
        this.isCodeSent = true;
        this.isLoading = false;
        this.startResendTimer();
        this.toastrService.success('Verification code sent to your email');
      },
      error => {
        this.isLoading = false;
        console.error('Send email code error:', error);
        console.error('Error details:', {
          status: error.status,
          message: error.message,
          error: error.error
        });

        // Handle specific error types
        if (error.status === 503 && error.error?.error === 'EMAIL_SERVICE_ERROR') {
          this.toastrService.error('Email service is not configured. Please contact your administrator.');
        } else if (error.status === 503 && error.error?.error === 'EMAIL_SEND_ERROR') {
          this.toastrService.error('Failed to send verification email. Please try again.');
        } else if (error.status === 401) {
          this.toastrService.error('Session expired. Please login again.');
          this.router.navigate(['/login']);
        } else {
          this.toastrService.error('Failed to send verification code. Please try again.');
        }
      }
    );
  }

  // Start countdown timer for resend button
  startResendTimer(): void {
    this.resendTimer = 60;
    this.resendInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  // Verify the entered code
  verifyCode(): void {
    if (this.verificationForm.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const code = this.verificationForm.value.code;
    const token = this.tempLoginData.tempToken || this.tempLoginData.mfaToken;

    this.http.post(`${environment.apiUrl}/mfa/verify-code`, {
      code,
      method: this.selectedMethod
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(
      (response: any) => {
        // MFA verification successful - complete login
        this.completeLogin(response);
      },
      error => {
        this.isLoading = false;
        this.verificationForm.reset();
      }
    );
  }

  // Complete the login process after successful MFA verification
  completeLogin(mfaResponse: any): void {
    // Store the final authentication data
    localStorage.setItem('ACCESS_TOKEN', JSON.stringify(mfaResponse.token));
    localStorage.setItem('role', JSON.stringify(mfaResponse.roleId));
    localStorage.setItem('websiteLanguage', mfaResponse.language || 'en');
    localStorage.setItem('user', JSON.stringify(mfaResponse));
    localStorage.setItem('modal', 'false');

    // Set login-app flag to show logout button in header
    localStorage.setItem('login-app', 'true');

    // Clear temporary MFA data
    localStorage.removeItem('temp_mfa_data');

    // Navigate to appropriate dashboard based on role
    this.navigateToUserDashboard(mfaResponse.roleId);

    this.toastrService.success('Login successful!');

    // Update Redux store
    this.store.dispatch(authActions.loginSuccess({
      user: mfaResponse
    }));
  }

  // Navigate to the appropriate dashboard based on user role
  navigateToUserDashboard(roleId: number): void {
    const role = String(roleId);
    if (role === '2') {
      this.router.navigateByUrl('/individual');
    } else if (role === '1') {
      this.router.navigateByUrl('/admin');
    } else if (role === '3' || role === '11') {
      this.router.navigateByUrl('/administrator');
    } else if (role === '4' || role === '10') {
      this.router.navigateByUrl('/professor');
    } else if (role === '6') {
      this.router.navigateByUrl('/vleresues');
    } else if (role === '7') {
      this.router.navigateByUrl('/super-admin');
    } else if (role === '5' || role === '9') {
      this.router.navigateByUrl('/student');
    } else {
      this.router.navigateByUrl('/');
    }
  }

  // Handle input formatting for backup codes
  onCodeInput(event: any): void {
    let value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (this.selectedMethod === 'BACKUP') {
      // Format as XXXXX-XXXXX for backup codes
      if (value.length > 5) {
        value = value.substring(0, 5) + '-' + value.substring(5, 10);
      }
    } else {
      // Only allow numbers for email/TOTP codes
      value = value.replace(/[^0-9]/g, '');
      if (value.length > 6) {
        value = value.substring(0, 6);
      }
    }

    this.verificationForm.patchValue({ code: value });
  }

  // Go back to login
  goBackToLogin(): void {
    // Clear all authentication-related localStorage items to prevent parsing errors
    localStorage.removeItem('temp_mfa_data');
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('modal');
    localStorage.removeItem('login-app');
    localStorage.removeItem('websiteLanguage');

    this.router.navigate(['/login']);
  }

  // Get form control for easy access
  get codeControl() {
    return this.verificationForm.get('code');
  }

  // Check if a method is available
  isMethodAvailable(method: 'EMAIL' | 'TOTP'): boolean {
    if (this.mfaMethod === 'BOTH') return true;
    return this.mfaMethod === method;
  }
}
